import React, { useState, useEffect } from 'react';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import ExportConfirmation from './ExportConfirmation';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import SearchableDropdown from './SearchableDropdown';
import { useLanguage } from '../LanguageContext';

const PackingMaterials = () => {
    const { t, isRtl } = useLanguage();
    const [received, setReceived] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        packagingMaterialId: '',
        supplierId: '',
        quantity: '',
        received_quantity: '',
        batch: '',
        manufacturingDate: '',
        receivingDate: new Date().toISOString().split('T')[0]
    });
    const [filterName, setFilterName] = useState('');
    const [filterSupplier, setFilterSupplier] = useState('');
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [exportConfirm, setExportConfirm] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [resReceived, resMaterials, resSuppliers] = await Promise.all([
                axios.get(`${API_BASE}/received-packaging`),
                axios.get(`${API_BASE}/packagingmaterials`),
                axios.get(`${API_BASE}/suppliers`)
            ]);
            setReceived(resReceived.data || []);
            setMaterials(resMaterials.data || []);
            setSuppliers(resSuppliers.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    const openAdd = () => {
        setEditing(false);
        setEditId(null);
        setForm({
            packagingMaterialId: '',
            supplierId: '',
            quantity: '',
            received_quantity: '',
            batch: '',
            manufacturingDate: '',
            receivingDate: new Date().toISOString().split('T')[0]
        });
        setModal(true);
    };

    const openEdit = (it) => {
        setEditing(true);
        setEditId(it._id);
        setForm({
            packagingMaterialId: it.packagingMaterialId?._id || '',
            supplierId: it.supplierId?._id || '',
            received_quantity: it.received_quantity || it.quantity || '',
            quantity: it.quantity || '',
            batch: it.batch || '',
            manufacturingDate: it.manufacturingDate ? it.manufacturingDate.split('T')[0] : '',
            receivingDate: it.receivingDate ? it.receivingDate.split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, quantity: Number(form.quantity) };
            // For new arrivals, received_quantity = quantity by default if not specified
            if (!editing) payload.received_quantity = payload.quantity;
            else payload.received_quantity = Number(form.received_quantity || form.quantity);

            if (editing) {
                await axios.put(`${API_BASE}/received-packaging/${editId}`, payload);
            } else {
                await axios.post(`${API_BASE}/received-packaging`, payload);
            }
            await fetchAll();
            setModal(false);
        } catch (err) {
            alert(err.response?.data?.msg || t.save_failed);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API_BASE}/received-packaging/${deleteConfirm.id}`);
            await fetchAll();
            setDeleteConfirm({ open: false, id: null, name: '' });
        } catch (err) {
            alert(err.response?.data?.msg || t.delete_failed);
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

    const filtered = received.filter(it => {
        const term = filterName.toLowerCase();
        return term === '' ||
            (it.packagingMaterialId?.name?.toLowerCase().includes(term)) ||
            (it.packagingMaterialId?.code?.toLowerCase().includes(term)) ||
            (it.batch?.toLowerCase().includes(term)) ||
            (it.supplierId?.supplier_name?.toLowerCase().includes(term)) ||
            (formatDate(it.manufacturingDate)?.toLowerCase().includes(term)) ||
            (formatDate(it.receivingDate)?.toLowerCase().includes(term));
    });

    const downloadCSV = () => {
        const headers = [t.packaging_material, t.code, t.supplier, t.batch, t.manufacture_date, t.received_quantity, t.remaining_quantity, t.date];
        const rows = filtered.map(it => [
            `"${it.packagingMaterialId?.name || t.deleted_material}"`,
            `"${it.packagingMaterialId?.code || '-'}"`,
            `"${it.supplierId?.supplier_name || t.deleted_supplier}"`,
            `"${it.batch || '-'}"`,
            `"${it.manufacturingDate ? formatDate(it.manufacturingDate) : '-'}"`,
            Number(it.received_quantity || it.quantity).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 }),
            Number(it.quantity).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 }),
            `"${formatDate(it.receivingDate)}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `packing_materials_${new Date().toISOString().slice(0, 10)}.CSV`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 4 };
    const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d2d2d2', fontSize: 13, outline: 'none', background: '#fff' };

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }} className="fade-in">
            {/* Page Title & Primary Action */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
                <h1 style={{
                    margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d'
                }}>{t.packing_materials}</h1>
                <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <button onClick={() => setExportConfirm(true)} style={{
                        padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2',
                        background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13,
                        cursor: 'pointer', transition: 'box-shadow 0.2s'
                    }}>
                        {t.export}
                    </button>
                    <button onClick={openAdd} style={{
                        padding: '8px 16px', borderRadius: 6, background: '#1976d2', color: '#fff',
                        fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
                        boxShadow: '0 1px 0 rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center'
                    }}>
                        {t.add_arrival}
                    </button>
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ebebeb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', background: '#fff', borderBottom: '1px solid #f1f1f1', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)', color: '#babfc3' }}>
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
                        </span>
                        <input
                            placeholder={t.filter_records}
                            value={filterName}
                            onChange={e => setFilterName(e.target.value)}
                            style={{ ...inputStyle, paddingLeft: isRtl ? 12 : 34, paddingRight: isRtl ? 34 : 12, textAlign: isRtl ? 'right' : 'left' }}
                        />
                    </div>

                </div>
                {loading ? <Loading message={t.syncing_records || "Syncing records..."} /> : (
                    <div className="fade-in" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
                            <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.packaging_material}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.code}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.supplier}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.batch}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.received_qty}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.remaining_qty}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.mfg_date}</th>
                                    <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.receiving_date}</th>
                                    <th style={{ width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_records_found}</td></tr>
                                ) : (
                                    filtered.map(it => (
                                        <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.packagingMaterialId?.name || 'Deleted Material'}</td>
                                            <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.packagingMaterialId?.code || '-'}</td>
                                            <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.supplierId?.supplier_name || 'Deleted Supplier'}</td>
                                            <td style={{ padding: '12px 16px', color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{it.batch || '-'}</td>
                                            <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(it.received_quantity || it.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600 }}>{Number(it.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.manufacturingDate ? formatDate(it.manufacturingDate) : '-'}</td>
                                            <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(it.receivingDate)}</td>
                                            <td style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                                                        setMenuOpenId(it._id);
                                                    }}
                                                    className="menu-btn"
                                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 8, borderRadius: 4 }}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Floating Menu */}
            {menuOpenId && (() => {
                const it = received.find(x => x._id === menuOpenId);
                if (!it) return null;
                return (
                    <div className="floating-menu" style={{
                        position: 'fixed',
                        [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
                        top: menuPos.y, background: '#fff',
                        borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
                        border: '1px solid #ebebeb'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
                            <button onClick={() => { setMenuOpenId(null); openEdit(it); }} style={{ padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent', cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4 }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit}</button>
                            <button onClick={() => { setMenuOpenId(null); setDeleteConfirm({ open: true, id: it._id, name: it.packagingMaterialId?.name }); }} style={{ padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent', cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4 }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete}</button>
                        </div>
                    </div>
                );
            })()}

            {modal && ReactDOM.createPortal(
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
                    <div className="modal-fade-in" style={{ width: 480, background: '#fff', borderRadius: 12, boxShadow: '0 28px 60px rgba(0,0,0,0.2)', overflow: 'hidden', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #ebebeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a1c1d' }}>{editing ? t.edit_record : t.record_arrival}</h2>
                            <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d7175' }}>
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={{ padding: 20 }}>
                                <div style={{ display: 'grid', gap: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.material_code}*</label>
                                            <SearchableDropdown
                                                showCheckbox={false}
                                                options={materials.map(m => ({ value: m._id, label: m.code || 'No Code' }))}
                                                value={form.packagingMaterialId}
                                                onChange={val => setForm({ ...form, packagingMaterialId: val })}
                                                placeholder={t.select_code}
                                            />
                                        </div>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.material_name}*</label>
                                            <SearchableDropdown
                                                showCheckbox={false}
                                                options={materials.map(m => ({ value: m._id, label: m.name }))}
                                                value={form.packagingMaterialId}
                                                onChange={val => setForm({ ...form, packagingMaterialId: val })}
                                                placeholder={t.select_material}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.supplier}*</label>
                                            <SearchableDropdown
                                                showCheckbox={false}
                                                options={suppliers.map(s => ({ value: s._id, label: s.supplier_name }))}
                                                value={form.supplierId}
                                                onChange={val => setForm({ ...form, supplierId: val })}
                                                placeholder={t.select_supplier}
                                            />
                                        </div>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.batch_lot_no}*</label>
                                            <input
                                                placeholder="e.g. B-901"
                                                value={form.batch}
                                                onChange={e => setForm({ ...form, batch: e.target.value })}
                                                style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }}
                                                required
                                            />
                                        </div>
                                    </div>
                                    {editing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                                <label style={labelStyle}>{t.received_qty}*</label>
                                                <input type="number" value={form.received_quantity} onChange={e => setForm({ ...form, received_quantity: e.target.value })} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                            </div>
                                            <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                                <label style={labelStyle}>{t.remaining_qty}*</label>
                                                <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.quantity_received}*</label>
                                            <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                        </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.mfg_date}</label>
                                            <input type="date" value={form.manufacturingDate} onChange={e => setForm({ ...form, manufacturingDate: e.target.value })} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                        </div>
                                        <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                            <label style={labelStyle}>{t.receiving_date}*</label>
                                            <input type="date" value={form.receivingDate} onChange={e => setForm({ ...form, receivingDate: e.target.value })} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ padding: '16px 20px', borderTop: '1px solid #ebebeb', display: 'flex', justifyContent: 'flex-end', gap: 12, background: '#f9fafb', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d2d2d2', background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', minWidth: 100 }}>
                                    {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : t.save}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {deleteConfirm.open && ReactDOM.createPortal(
                <DeleteConfirmation
                    itemName={deleteConfirm.name}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
                    isDeleting={deleting}
                />,
                document.body
            )}

            {exportConfirm && ReactDOM.createPortal(
                <ExportConfirmation
                    itemName={t.packing_materials}
                    onConfirm={() => { downloadCSV(); setExportConfirm(false); }}
                    onCancel={() => setExportConfirm(false)}
                />,
                document.body
            )}
        </div>
    );
};

export default PackingMaterials;
